import _orderBy from 'lodash/orderBy'
import _uniqBy from 'lodash/uniqBy'
import dates from 'frontcore/utils/dates'
import FactoryLevel from 'frontend/helpers/factoryLevel'
import {
  copyStandard,
  createStandard,
  deleteStandard,
  getStandard,
  moveStandard,
  updateStandard,
} from 'frontend/communications/standards'
import { FORMAT_MIN } from 'frontend/constants/standards'
import { PROMISE_STATUS } from 'frontcore/constants/promises'
import { useStandardsStore } from 'frontcore/store/standards/useStandardsStore'

const stateObj = {
  standards: [],
  allVersionStandards: [],
  standardsLoadingStatus: PROMISE_STATUS.notStarted,
}

const unParseStandardsAPIResponse = (standardAPIResponse) => {
  return standardAPIResponse.map(standard => {
    return {
      id: standard.id,
      created: standard.created,
      updated: standard.updated,
      approved_date: standard.approvedDate,
      ascendants: standard.ascendants,
      first_version_created: standard.firstVersionCreated,
      is_active: standard.isActive,
      is_auditable: standard.isAuditable,
      is_beginner_friendly: standard.isBeginnerFriendly,
      is_bottleneck: standard.isBottleneck,
      is_in_paper: standard.isInPaper,
      is_minor_version: standard.isMinorVersion,
      name: standard.name,
      perimeters: standard.perimeters,
      version_number: standard.versionNumber,
    }
  })
}

const getters = {
  getStandards (state) {
    return state.standards
  },
  getAllVersionStandards (state) {
    return state.allVersionStandards
  },
  getStandardsByPerimeter: (state) => (perimeterId) => {
    return state.standards.filter(standard => standard.perimeters.includes(perimeterId))
  },
  getStandardsByPerimeterIds: (state) => (perimeterIds) => {
    return state.standards.filter(standard => standard.perimeters.some((standardPerimeterId) => {
      return perimeterIds.includes(standardPerimeterId)
    }))
  },
  getStandardById: (state) => (id) => {
    return state.allVersionStandards.find(standard => standard.id === id)
  },
  standardsAreLoaded (state) {
    return state.standardsLoadingStatus === PROMISE_STATUS.complete
  },
  getStandardByParentId: (state) => (id) => {
    return state.standards.find(standard => standard.ascendants.indexOf(id) !== -1)
  },
  iluEvaluablePeople (state, globalGetters) {
    return globalGetters.getPeople.filter(
      person => person.evaluate_skills || person.is_operator || person.is_polyvalent_in_some_perimeters
    )
  },
}

const mutations = {
  setStandard (state, standard) {
    state.standards = [
      ...state.standards.filter(std => std.id !== standard.id),
      standard,
    ]

    if (standard.is_active) {
      state.allVersionStandards = [
        ...state.allVersionStandards.filter(std => std.id !== standard.id),
        standard,
      ]
    }
  },
  setStandards (state, standards) {
    const standardsWithoutDuplicates = _uniqBy(standards, standard => standard.id)
    const onlyActiveStandards = standardsWithoutDuplicates.filter((standard) => standard.is_active)
    state.standards = _orderBy(onlyActiveStandards, 'name')
    state.allVersionStandards = _orderBy(standardsWithoutDuplicates, 'name')
  },
  setAllVersionStandards (state, value) {
    state.allVersionStandards = _orderBy(value, 'name')
  },
  setStandardsLoadingStatus (state, status) {
    state.standardsLoadingStatus = status
  },
  removeStandard (state, standardId) {
    const standardToRemove = state.standards.find(std => std.id === standardId)
    if (standardToRemove) {
      state.standards = state.standards.filter(std => std !== standardToRemove)
    }
    const allVersionStandardToRemove = state.allVersionStandards.find(std => std.id === standardId)
    if (allVersionStandardToRemove) {
      state.allVersionStandards = state.allVersionStandards.filter(std => std !== allVersionStandardToRemove)
    }
  },
}

const actions = {
  createStandard ({ commit, rootGetters }, payload) {
    payload.ppes = payload.ppes ?? []
    payload.device_created = dates.getFactoryUTCDate()
    payload.created_by = payload.created_by ?? rootGetters.userId
    return createStandard(payload).then(standard => {
      commit('setStandard', standard)
      const factoryLevel = FactoryLevel.getFactoryLevelsFilteredByType(FactoryLevel.STANDARD).at(0)
      if (factoryLevel) {
        standard.perimeters.forEach(perimeter => {
          factoryLevel.addItem({
            id: standard.id,
            name: standard.name,
            parent: perimeter,
            isInPaper: standard.is_in_paper,
          })
        })
      }
      return standard
    })
  },
  async fetchStandards ({ commit }, payload = {}) {
    commit('setStandardsLoadingStatus', PROMISE_STATUS.pending)
    await useStandardsStore().fetchStandards(payload)
    const unParsedStandards = unParseStandardsAPIResponse(useStandardsStore().standards)
    commit('setStandards', unParsedStandards)
    commit('setStandardsLoadingStatus', PROMISE_STATUS.complete)
    return unParsedStandards
  },
  fetchStandard ({ commit }, standardId) {
    return getStandard(standardId, FORMAT_MIN).then(standard => {
      commit('setStandard', standard)
    })
  },
  moveStandard ({ dispatch }, { standardId, payload }) {
    return moveStandard (standardId, payload)
      .then(movedStandard => {
        dispatch('fetchStandard', standardId)
        return movedStandard
      })
  },
  copyStandardTogether ({ dispatch }, { standardId, payload }) {
    return updateStandard (standardId, payload)
      .then(copiedStandard => {
        dispatch('fetchStandard', standardId)
        return copiedStandard
      })
  },
  copyStandardIndependently ({ dispatch }, { standardId, payload }) {
    return copyStandard (standardId, payload)
      .then(copiedStandard => {
        dispatch('fetchStandard', standardId)
        dispatch('fetchStandard', copiedStandard.id)
        return copiedStandard
      })
  },
  removeStandard ({ commit }, standardId) {
    return deleteStandard (standardId)
      .then(removeStandard => {
        commit('removeStandard', standardId)
        return removeStandard
      })
  },
  removeStandardFromPerimeter ({ dispatch }, { standardId, payload }) {
    return updateStandard (standardId, payload)
      .then(removedStandard => {
        dispatch('fetchStandard', standardId)
        return removedStandard
      })
  },
}

export default {
  getters,
  mutations,
  state: stateObj,
  actions,
}
